export const DEFAULT_CONTRACT_DOCUMENT_INFO = Object.freeze({
  kurum_unvani: 'YED KURS',
  kurum_adresi: '',
  kurum_telefonu: '',
  kurum_vergi_no: '',
  sozlesme_no: '',
  duzenleme_yeri: 'Osmaniye',
  odeme_yeri: 'Osmaniye',
  borclu_ad_soyad: '',
  borclu_tc_kimlik: '',
  borclu_adres: '',
  borclu_telefon: '',
  borclu_is_adresi: '',
  dogum_yeri: '',
  sube: '',
  pesinat: '',
  burs_yuzdesi: '',
  kefil_ad_soyad: '',
  kefil_tc_kimlik: '',
  kefil_adres: '',
  kitap_ucreti: '',
  yayin_ucreti: '',
  kdv_orani: '',
});

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const money = value => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
}).format(Number(value) || 0);

const dateText = value => {
  if (!value) return '-';
  const text = String(value).slice(0, 10);
  const [year, month, day] = text.split('-').map(Number);
  if (year && month && day) return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
  return new Date(value).toLocaleDateString('tr-TR');
};

const isoToday = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const addMonths = (date, months) => {
  const [year, month, day] = String(date || isoToday()).slice(0, 10).split('-').map(Number);
  const targetMonthIndex = month - 1 + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return [
    String(targetYear).padStart(4, '0'),
    String(targetMonth + 1).padStart(2, '0'),
    String(Math.min(day, lastDay)).padStart(2, '0'),
  ].join('-');
};

export const getContractStudentName = (info = {}) => (
  [info.ad, info.soyad].filter(Boolean).join(' ').trim()
  || info.ad_soyad
  || info.ogrenci_ad_soyad
  || '-'
);

export const normalizeContractDocumentInfo = (value = {}, student = {}, contract = {}) => {
  let parsed = value;
  if (typeof value === 'string') {
    try { parsed = JSON.parse(value); } catch { parsed = {}; }
  }
  const parentName = student.veli_adi || student.veli_ad_soyad || student.anne_adi || student.baba_adi || '';
  return {
    ...DEFAULT_CONTRACT_DOCUMENT_INFO,
    ...(parsed && typeof parsed === 'object' ? parsed : {}),
    kurum_unvani: 'YED KURS',
    sozlesme_no: parsed?.sozlesme_no || contract.id || contract.sozlesme_id || '',
    borclu_ad_soyad: parsed?.borclu_ad_soyad || parentName,
    borclu_adres: parsed?.borclu_adres || student.adres || '',
    borclu_telefon: parsed?.borclu_telefon || student.veli_telefon || student.veli_tel || '',
  };
};

export const buildInstallmentPlan = form => {
  if (form.taksit_modu === 'manuel' && Array.isArray(form.taksitler) && form.taksitler.length) {
    return form.taksitler.map((installment, index) => ({
      no: installment.taksit_no || installment.no || index + 1,
      date: installment.vade_tarihi || installment.date,
      amount: Number(installment.tutar ?? installment.amount) || 0,
      durum: installment.durum,
      odeme_tarihi: installment.gercek_odeme || installment.odeme_tarihi,
      odeme_yontemi: installment.odeme_yontemi,
      odeme_tutari: installment.odeme_tutari,
      kalan_tutar: installment.kalan_tutar,
    }));
  }

  const count = form.odeme_tipi === 'pesin' ? 1 : Math.max(1, Number.parseInt(form.taksit_sayisi, 10) || 1);
  const total = Number.parseFloat(form.net_ucret) || 0;
  const base = Math.floor((total / count) * 100) / 100;
  let used = 0;
  return Array.from({ length: count }, (_, index) => {
    const amount = index === count - 1 ? Math.max(0, total - used) : base;
    used += amount;
    return {
      no: index + 1,
      date: addMonths(form.ilk_odeme_tarihi || form.baslangic_tarihi, index),
      amount,
      durum: 'bekliyor',
    };
  });
};

const getInstallmentPaidAmount = installment => {
  const amount = Math.max(0, Number(installment?.amount) || 0);
  const hasPaidAmount = installment?.odeme_tutari !== undefined
    && installment?.odeme_tutari !== null
    && installment?.odeme_tutari !== '';
  if (hasPaidAmount) {
    return Math.min(amount, Math.max(0, Number(installment.odeme_tutari) || 0));
  }

  const hasRemainingAmount = installment?.kalan_tutar !== undefined
    && installment?.kalan_tutar !== null
    && installment?.kalan_tutar !== '';
  if (hasRemainingAmount) {
    const remaining = Math.min(amount, Math.max(0, Number(installment.kalan_tutar) || 0));
    return Math.max(0, amount - remaining);
  }

  return installment?.durum === 'odendi' ? amount : 0;
};

const buildCollectionHistory = payments => {
  const groups = new Map();
  (Array.isArray(payments) ? payments : []).forEach((payment, index) => {
    const key = payment.tahsilat_grup_uuid || `odeme-${payment.id || index}`;
    const current = groups.get(key) || {
      id: payment.id || index,
      tarih: payment.odeme_tarihi,
      tutar: 0,
      odeme_yontemi: payment.odeme_yontemi,
      makbuz_no: payment.makbuz_no,
      notlar: payment.notlar,
    };
    current.tutar += Number(payment.tutar || 0);
    groups.set(key, current);
  });
  return [...groups.values()].sort((a, b) => {
    const dateDiff = String(a.tarih || '').localeCompare(String(b.tarih || ''));
    return dateDiff || Number(a.id || 0) - Number(b.id || 0);
  });
};

const checkbox = checked => `<span class="check">${checked ? '✓' : ''}</span>`;

const commonDocumentStyles = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; color: #121826; background: #eceff3; font-family: Arial, Helvetica, sans-serif; }
  .toolbar { max-width: 210mm; margin: 12px auto; display: flex; justify-content: flex-end; gap: 8px; }
  .toolbar button { border: 0; border-radius: 7px; padding: 10px 16px; background: #c72e45; color: white; font-weight: 800; cursor: pointer; }
  @media print { body { background: white; } .toolbar { display: none; } }
`;

export const buildCourseContractHtml = ({ form, student, logoUrl = '' }) => {
  const info = student || form.sozlesme_form_bilgileri || {};
  const documentInfo = normalizeContractDocumentInfo(form.belge_bilgileri || form.belge_bilgileri_json, info, form);
  const installments = buildInstallmentPlan(form);
  const parentName = documentInfo.borclu_ad_soyad || info.veli_adi || info.veli_ad_soyad || info.anne_adi || info.baba_adi || '';
  const studentName = getContractStudentName(info);
  const className = info.sinif_adi || info.sinif || '';
  const birthDate = info.dogum_tarihi || info.ogrenci_dogum_tarihi;
  const birthYear = birthDate ? String(birthDate).slice(0, 4) : '';
  const birthPlaceAndYear = [documentInfo.dogum_yeri, birthYear].filter(Boolean).join(' / ');
  const totalFee = Number(form.net_ucret) || 0;
  const bookFee = Math.min(totalFee, Math.max(0, Number(documentInfo.kitap_ucreti) || 0));
  const publicationFee = Math.min(totalFee - bookFee, Math.max(0, Number(documentInfo.yayin_ucreti) || 0));
  const educationFee = totalFee - bookFee - publicationFee;
  const institution = escapeHtml(documentInfo.kurum_unvani);
  const field = value => value === undefined || value === null || value === '' ? '&nbsp;' : escapeHtml(value);
  const branch = String(documentInfo.sube || '').toLocaleLowerCase('tr-TR');
  const scholarshipRate = Number(documentInfo.burs_yuzdesi) || 0;
  const hasDiscount = Number(form.indirim_tutar) > 0 || Number(form.indirim_yuzde) > 0;
  const paymentRows = Array.from({ length: 12 }, (_, index) => {
    const row = installments[index];
    const paidAmount = row ? getInstallmentPaidAmount(row) : 0;
    return `<tr>
      <td>${index + 1}. &nbsp; Taksit</td>
      <td>${row ? dateText(row.date) : '&nbsp;'}</td>
      <td>${row ? money(row.amount) : '&nbsp;'}</td>
      <td>${paidAmount ? money(paidAmount) : '&nbsp;'}</td>
      <td>${field(row?.odeme_yontemi)}</td>
      <td>${row?.odeme_tarihi ? dateText(row.odeme_tarihi) : '&nbsp;'}</td>
      <td>&nbsp;</td>
    </tr>`;
  }).join('');
  const extraPaymentRows = installments.slice(12).map((row, offset) => {
    const index = offset + 12;
    const paidAmount = getInstallmentPaidAmount(row);
    return `<tr>
      <td>${index + 1}. &nbsp; Taksit</td>
      <td>${dateText(row.date)}</td>
      <td>${money(row.amount)}</td>
      <td>${paidAmount ? money(paidAmount) : '&nbsp;'}</td>
      <td>${field(row.odeme_yontemi)}</td>
      <td>${row.odeme_tarihi ? dateText(row.odeme_tarihi) : '&nbsp;'}</td>
      <td>&nbsp;</td>
    </tr>`;
  }).join('');
  const extraPaymentPlanPage = extraPaymentRows ? `
      <section class="contract-page"><div class="photo-sheet">
        <div class="page-two-title">${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Kurum logosu" />` : ''} ÖDEME PLANI DEVAMI</div>
        <table class="plan-exact">
          <thead><tr><th>ÖDEME PLANI</th><th>TARİH</th><th>ÖDENECEK</th><th>ÖDENEN</th><th>ÖDEME HESABI</th><th>ÖDEME TARİHİ</th><th>FATURA NO</th></tr></thead>
          <tbody>${extraPaymentRows}</tbody>
        </table>
        <div class="contract-signatures" style="margin-top: 8mm">
          <div><strong>Öğrenci / Öğrenci Veli</strong><span>Ad Soyad: ${field(parentName)}</span><span>İmza:</span></div>
          <div><strong>Kurumun (Kaşe, Mühür ve Yetkili İmza)</strong><span>${institution}</span>${logoUrl ? `<img class="signature-logo" src="${escapeHtml(logoUrl)}" alt="Kurum logosu" />` : ''}</div>
        </div>
      </div></section>` : '';

  return `<!doctype html>
    <html lang="tr"><head><meta charset="utf-8" />
      <title>Öğrenci Kayıt Sözleşmesi - ${escapeHtml(studentName)}</title>
      <style>
        ${commonDocumentStyles}
        @page { size: A4 portrait; margin: 0; }
        .contract-page { width: 210mm; height: 297mm; margin: 0 auto 10px; padding: 6mm; background: #fff; page-break-after: always; break-after: page; overflow: hidden; }
        .photo-sheet { height: 285mm; border: 2.2mm solid #c72e45; padding: 4.5mm; position: relative; color: #171923; }
        .photo-header { display: grid; grid-template-columns: 25mm 1fr 25mm; align-items: start; text-align: center; min-height: 48mm; }
        .meb-seal { width: 22mm; height: 22mm; border: 1.4px solid #8c8c8c; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; color: #777; font-size: 7pt; font-weight: 800; line-height: 1.05; }
        .photo-title { font-weight: 900; font-size: 10.5pt; line-height: 1.12; }
        .photo-title .main-title { font-size: 11.5pt; }
        .photo-title .institution-name { margin-top: 4mm; font-size: 9.2pt; }
        .photo-title .year { font-size: 9pt; }
        .center-logo { height: 18mm; margin-top: 3mm; }
        .center-logo img { max-height: 18mm; max-width: 32mm; object-fit: contain; }
        .top-strip { background: #d7dcda; display: grid; grid-template-columns: 2fr 1fr; font-size: 9pt; font-weight: 900; padding: 1mm 2mm; }
        .student-parent-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4mm; margin: 1.5mm 0 2mm; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .form-table { font-size: 8pt; }
        .form-table td { border: 1px solid #555963; height: 6mm; padding: .8mm 1.5mm; }
        .form-table td:first-child { width: 43%; font-weight: 700; }
        .form-table tr:nth-child(even) td { background: #efd0d5; }
        .form-table .tall td { height: 11mm; }
        .dark-bar { background: #2e3040; color: white; font-size: 10pt; font-weight: 900; text-align: center; padding: 1mm; }
        .payment-info { font-size: 8pt; }
        .payment-info td { border: 1px solid #555963; height: 5.5mm; padding: .7mm 1.5mm; }
        .payment-info td:first-child { width: 47%; font-weight: 700; }
        .payment-info tr:nth-child(even) td { background: #efd0d5; }
        .check { display: inline-flex; width: 4mm; height: 4mm; border: 1px solid #34394c; align-items: center; justify-content: center; margin: 0 1mm; font-size: 8pt; font-weight: 900; vertical-align: middle; }
        .terms-heading { background: #d7dcda; font-size: 8.6pt; font-weight: 900; padding: .8mm 1mm; margin-top: 1.5mm; }
        .photo-terms { font-size: 6.7pt; line-height: 1.18; margin: 1mm 0 0; padding-left: 5.5mm; text-align: justify; }
        .photo-terms li { margin-bottom: .7mm; padding-left: .6mm; }
        .page-two-title { height: 9mm; background: #c72e45; color: white; display: flex; align-items: center; justify-content: center; font-size: 14pt; font-weight: 900; margin-bottom: 2mm; position: relative; }
        .page-two-title img { position: absolute; left: 3mm; height: 7mm; max-width: 22mm; object-fit: contain; }
        .fees-exact { font-size: 8pt; margin-bottom: 3mm; }
        .fees-exact th, .fees-exact td { border: 1px solid #555963; height: 5mm; padding: .5mm 1.2mm; }
        .fees-exact th { background: #303241; color: white; text-transform: uppercase; }
        .plan-exact { font-size: 6.8pt; }
        .plan-exact th, .plan-exact td { border: 1px solid #555963; height: 5.2mm; padding: .5mm .8mm; text-align: center; }
        .plan-exact th { background: #303241; color: white; font-size: 6.4pt; text-transform: uppercase; }
        .plan-exact th:first-child, .plan-exact td:first-child { width: 15%; }
        .refund-terms { font-size: 6.35pt; line-height: 1.14; margin-top: .8mm; }
        .refund-terms li { margin-bottom: .45mm; }
        .refund-terms ol { margin: .5mm 0 .5mm; padding-left: 4mm; list-style-type: lower-alpha; }
        .contract-signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 18mm; margin-top: 2mm; border-top: 2mm solid #d7dcda; padding-top: 1mm; font-size: 8.5pt; min-height: 23mm; }
        .contract-signatures > div { display: flex; flex-direction: column; gap: 2mm; }
        .contract-signatures > div:last-child { text-align: center; }
        .signature-logo { display: block; height: 10mm; max-width: 28mm; object-fit: contain; margin: 0 auto; }
        ${promissoryNotesStyles}
        .combined-notes { page-break-before: always; break-before: page; }
        @media print { .contract-page { margin: 0; } }
      </style></head><body>
      <section class="contract-page"><div class="photo-sheet">
        <header class="photo-header">
          <div class="meb-seal">T.C.<br />MEB</div>
          <div class="photo-title">
            <div>T.C. MİLLÎ EĞİTİM BAKANLIĞI</div>
            <div>ÖZEL ÖĞRETİM KURUMLARI GENEL MÜDÜRLÜĞÜ</div>
            <div class="main-title">ÖĞRENCİ KAYIT SÖZLEŞMESİ</div>
            <div class="institution-name">${institution}</div>
            <div class="year">${field(form.egitim_yili)} ÖĞRETİM YILI</div>
            <div class="center-logo">${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Kurum logosu" />` : ''}</div>
          </div>
          <div class="meb-seal">T.C.<br />MEB</div>
        </header>
        <div class="top-strip"><div>ÖZEL KURUM ADI: ${institution}</div><div>SINIFI: ${field(className)}</div></div>
        <div class="student-parent-grid">
          <table class="form-table"><tbody>
            <tr><td>T.C. Kimlik No</td><td>${field(info.tc_kimlik)}</td></tr>
            <tr><td>Öğrenci Adı Soyadı</td><td>${field(studentName === '-' ? '' : studentName)}</td></tr>
            <tr><td>Doğum Yeri ve Yılı</td><td>${field(birthPlaceAndYear)}</td></tr>
            <tr class="tall"><td>Ev Adresi</td><td>${field(info.adres)}</td></tr>
            <tr><td>Şube</td><td>${checkbox(branch === 'avm')} AVM &nbsp;&nbsp; ${checkbox(branch === 'bulvar')} Bulvar</td></tr>
          </tbody></table>
          <table class="form-table"><tbody>
            <tr><td>T.C. Kimlik No</td><td>${field(documentInfo.borclu_tc_kimlik)}</td></tr>
            <tr><td>Ad ve Soyad</td><td>${field(parentName)}</td></tr>
            <tr><td>Mesleği</td><td>${field(info.veli_meslek)}</td></tr>
            <tr><td>Anne Telefon</td><td>${field(info.anne_telefon)}</td></tr>
            <tr><td>Baba Telefon</td><td>${field(info.baba_telefon)}</td></tr>
            <tr><td>Ev Adresi</td><td>${field(documentInfo.borclu_adres)}</td></tr>
            <tr><td>İş Adresi</td><td>${field(documentInfo.borclu_is_adresi)}</td></tr>
            <tr><td>E-Posta</td><td>${field(info.veli_email)}</td></tr>
          </tbody></table>
        </div>
        <div class="dark-bar">ÖDEME BİLGİLERİ</div>
        <table class="payment-info"><tbody>
          <tr><td>Kurumun İlan Ettiği Yıllık Kitap Ücreti</td><td>${bookFee ? money(bookFee) : '&nbsp;'}</td></tr>
          <tr><td>Öğrencinin Kayıt Edildiği Eğitim Öğretim Ücreti</td><td>${money(totalFee)}</td></tr>
          <tr><td>Ödeme Şekli</td><td>${checkbox(form.odeme_tipi === 'pesin')} Peşin &nbsp;&nbsp;&nbsp; ${checkbox(form.odeme_tipi !== 'pesin')} Taksit</td></tr>
          <tr><td>Taksit Başlangıç Tarihi</td><td>${dateText(form.ilk_odeme_tarihi || installments[0]?.date)}</td></tr>
          <tr><td>Peşinat</td><td>${documentInfo.pesinat ? money(documentInfo.pesinat) : '&nbsp;'}</td></tr>
          <tr><td>Taksit Sayısı ve Tutarı</td><td>${installments.length} Taksit / ${money(totalFee)}</td></tr>
          <tr><td>Eğitim Bursu Alıyor Mu?</td><td>${checkbox(scholarshipRate > 0)} Evet &nbsp;&nbsp;&nbsp; ${checkbox(scholarshipRate <= 0)} Hayır</td></tr>
          <tr><td>Eğitim Bursu Alıyorsa Yüzdesi</td><td>${scholarshipRate > 0 ? `%${scholarshipRate}` : '&nbsp;'}</td></tr>
          <tr><td>İndirim Alıyor Mu?</td><td>${checkbox(hasDiscount)} Evet &nbsp;&nbsp;&nbsp; ${checkbox(!hasDiscount)} Hayır</td></tr>
          <tr><td>İndirim Kalemi</td><td>${field(form.indirim_tipi)}</td></tr>
        </tbody></table>
        <div class="terms-heading">KAYIT İLE İLGİLİ ESASLAR</div>
        <ol class="photo-terms">
          <li>${institution}, geliştirmiş olduğu tüm programlarını, sınavlarını ve test sistemini eksiksiz olarak öğrenciye sunacaktır.</li>
          <li>${institution} merkezimize kayıtlı öğrencilerin ilgili programa devam etmesi temel esastır. Devamsızlık yapan öğrencilerin velileri/vasileri, öğrenci mazeretlerini kuruma bildirir. Velisi/vasilerce söz konusu bildirimin kuruma yapılmaması halinde, kurum idaresi gelmeyen öğrencilerin velisi/vasisiyle iletişim kurarak devamsız öğrenci hakkında velisi/vasisine bilgi verir.</li>
          <li>Kurum öğrencinin fotoğrafını, çalışmalarını, başarısını v.s. tüm bilgilerini reklam çalışmalarında ve tanıtım alanlarında ücretsiz kullanabilir.</li>
          <li>Fiziken ve fiilen eğitim imkânını ortadan kaldıran mücbir sebep hallerinde (deprem, savaş, afet, millî seferberlik, salgın hastalık, Bakanlık kararı, her tür resmî yasaklama vb.); kurum mücbir sebebin başlamasından itibaren 10 iş günü içinde işbu sözleşmeden kaynaklanan tüm eğitim edimlerini uzaktan eğitim olarak ifa edecektir. Veli kurumun bu hakkını hiçbir itiraza yer olmayacak şekilde gayrikabili rücu peşinen kabul etmektedir.</li>
          <li>Eğitim öğretim sözleşme bedeli ${installments.length} aylık eğitim öğretim süresince her ay faturalandırılır.</li>
          <li>Eğitim öğretim süresince öğrenci tarafından kurum araç gereçlerine verilebilecek zararların tazmini öğrencinin velisi tarafından karşılanır.</li>
          <li>Zamanında yapılmayan ödemeler için veliye 5 iş günü süre verilir. Bu süre sonunda ödeme yapılmamışsa tüm senetler vadelerine bakılmaksızın muaccel hale gelir.</li>
          <li>Ödeme planı uyarınca yapılması gereken ödemelerde herhangi bir gecikme olursa öğrenci velisi aylık %10 gecikme faizi ödemeyi kabul eder.</li>
          <li>Öğrenci/öğrenci velisi her hâlükârda 30 iş günü boyunca ödemelerini geciktirmesi halinde, kurumun öğrencinin eğitimini durdurma hakkı olduğunu kabul eder.</li>
          <li>Öğrenci/öğrenci velisi kendisine verilen uzaktan eğitim portalı şifrelerini, sanal sınıf vb. şifresini başkaları ile paylaşamaz; paylaşırsa tüm kanuni sorumluluğu kabul eder, yıllık ücretin tamamı tahsil edilir.</li>
        </ol>
      </div></section>
      <section class="contract-page"><div class="photo-sheet">
        <div class="page-two-title">${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Kurum logosu" />` : ''} ÖDEME PLANI</div>
        <table class="fees-exact">
          <thead><tr><th>ÜCRET KALEMLERİ</th><th>ÜCRET</th><th>KDV</th></tr></thead>
          <tbody>
            <tr><td>Eğitim Ücreti</td><td>${money(educationFee)}</td><td>${field(documentInfo.kdv_orani)}</td></tr>
            <tr><td>Yayın Ücreti</td><td>${publicationFee ? money(publicationFee) : '&nbsp;'}</td><td>${field(documentInfo.kdv_orani)}</td></tr>
            <tr><td><strong>TOPLAM</strong></td><td><strong>${money(totalFee)}</strong></td><td>&nbsp;</td></tr>
          </tbody>
        </table>
        <table class="plan-exact">
          <thead><tr><th>ÖDEME PLANI</th><th>TARİH</th><th>ÖDENECEK</th><th>ÖDENEN</th><th>ÖDEME HESABI</th><th>ÖDEME TARİHİ</th><th>FATURA NO</th></tr></thead>
          <tbody>${paymentRows}</tbody>
        </table>
        <div class="terms-heading">KAYIT SİLME VE ÜCRET İADESİ KAYIT İLE İLGİLİ ESASLAR</div>
        <ol class="photo-terms refund-terms">
          <li>Kayıt sildirme ve ücret iadelerinde eğitim bedeli hariç belirlenmiş olan kitap ücreti hiçbir şekilde geri iade edilmez, iadesi talep edilemez.</li>
          <li>Kayıt sildirme ve ücret iadelerinde, kayıt sildirme sebebi her ne olursa olsun, iade edilecek kısım, sözleşmede belirtilmiş olan toplam eğitim bedelinin %10'u oranında kesinti yapılarak hesap edilecektir.</li>
          <li>Öğrenim dönemi içinde zorunlu bir sebeple kayıt sildirmek isteyen öğrenci için, öğrenci/öğrenci velisinin şube müdürlüğüne yazılı talep dilekçesi vermesi ve mazeretine ilişkin resmî belgeyi de ekleyerek ibraz etmesi gerekir.</li>
          <li>Öğrenim dönemi içinde kayıt sildirmek isteyen öğrenci/öğrenci velisinin aşağıda belirtilen sebeplerden biri veya birkaçı ile ilgili belge ibraz etmeleri zorunludur:
            <ol><li>Öğrencinin il dışına başka bir okula nakil olması (resmî belge).</li><li>Öğrencinin sağlığının kursa devam edemeyecek duruma gelmesinin sağlık kurulu raporu ile belgelenmesi.</li><li>Öğrenci/öğrenci velisinin iflas etmesi (öğrencinin ${institution} merkezinde okutulamayacak duruma düştüğünü gösterir resmî kurumlardan belgelendirilmesi).</li></ol>
          </li>
          <li>Öğretim yılı başlamadan ${institution} merkezinden ayrılanlardan, belirlenen toplam eğitim ücretinin %10'u kesinti yapılarak iade edilir.</li>
          <li>Bunun haricinde ${institution} merkezinin M.E.B. tarafından yeni yayımlanan kararname ve kanunlar, eğitim veren kurumun özel nedenlerden dolayı elde olmayan sebepler nedeniyle kapanması halinde; öğrenci kaydı otomatik olarak bu tarihte silinmiş sayılarak, o tarihe kadar olan eğitim bedelleri mahsup edilerek varsa kalan bakiye veliye iade olunur. Veli o tarihe kadar olan tüm eğitim bedellerinden dolayı kuruma borç mesuliyetinin devam ettiğini kabul etmektedir. Ancak kurumun kapatılması dışında kalan ve yukarıda sayılan mücbir haller dahilinde, eğitimin uzaktan eğitim olarak sürdürülebildiği haller bu durumun dışında olup, veli mücbir sebep hallerinde kurumun uzaktan eğitim verebileceğini ve bu durumun sözleşme iptal sebebi sayılamayacağını, geriye dönük verilmiş olan fiilî eğitim bedellerinin geri iadesini talep hakkı doğurmayacağını peşinen kabul etmektedir.</li>
          <li>Kaydının silinmesine karar verilen öğrencinin eğitim almış olduğu dönemlere ait kurumumuza ödeyeceği bakiye ücretleri; dönem başlangıcı esnasında faturalandırılmış olan ${bookFee + publicationFee ? money(bookFee + publicationFee) : '........ TL'} + KDV kitap yayın ücreti düşülerek, kayıt esnasında belirlenen toplam eğitim bedelinin ${installments.length} aya bölünerek kalan aylık bakiyesi hesabıyla eğitim gördüğü ve ödemediği aylar üzerinden tahsil edilir.</li>
          <li>Kaydının silinmesine karar verilen öğrencinin, eğer peşin ödeme yapılmışsa, geri iade alacağı kısım ile ilgili hesaplamada ise; dönem başlangıcı esnasında faturalandırılmış olan ${bookFee + publicationFee ? money(bookFee + publicationFee) : '........ TL'} + KDV kitap yayın ücreti düşülerek, kalan bakiyeden toplam eğitim ücretinin %10 oranında kesinti yapıldıktan sonra belirlenecek olan aylık miktar esas alınır. Peşin ödeme yapılmışsa dönem başında peşin ödeme yapan öğrencinin kayıt iptali halinde, kitap yayın bedeli düşüldükten sonra kalan toplam eğitim bedeli üzerinden her ay için belirlenen bir aylık eğitim bedeli bulunarak; öğrenci eğitim almamış ise ödemiş olduğu bedelden toplam eğitim bedelinin %10'u oranında tekabül eden miktar düşülerek geri ödemesi yapılır. Eğitim almaya başladı ise ödemiş olduğu bedelden, toplam eğitim bedelinin %10'u oranında tekabül eden miktar ile o dönemde almış olduğu ders bedeli düşüldükten sonra kalan kısım kendisine iade edilecektir. Öğrenci/öğrenci velisi iade bedeli konusunda bu hesaplamayı kabul etmekte olduğunu hiçbir itiraza yer olmaksızın beyan eder.</li>
          <li>Kayıt sildirme sebebiyle yapılacak iadelerde iptal tarihi hesaplanması; kayıt sildirme talebi onay tarihinin denk geldiği ayın sonu olarak değerlendirilir.</li>
          <li>İade işlemlerinde kredi kartı ya da banka kredisi ile yapılmış ödemelerden dolayı söz konusu olan ödemelerdeki banka komisyon oranları tutarları düşülerek yapılacaktır. İadeler takip eden ayın 30'una kadar gerçekleşir.</li>
          <li>Belirtilen şartlar gerçekleşmeden ve öğrenci kaydını silmeden önce kayıt sildirmek isteyen öğrenci/öğrenci velisi, ${institution} merkezini bırakan öğrenciden yıllık ücretin tamamı tahsil edilir.</li>
          <li>Bu sözleşmeden kaynaklanan tüm anlaşmazlıklarda ${field(documentInfo.duzenleme_yeri || 'Osmaniye')} İcra Daireleri ve Mahkemeleri yetkilidir.</li>
        </ol>
        <div class="contract-signatures">
          <div><strong>Öğrenci / Öğrenci Veli</strong><span>Ad Soyad: ${field(parentName)}</span><span>İmza:</span></div>
          <div><strong>Kurumun (Kaşe, Mühür ve Yetkili İmza)</strong><span>${institution}</span>${logoUrl ? `<img class="signature-logo" src="${escapeHtml(logoUrl)}" alt="Kurum logosu" />` : ''}</div>
        </div>
      </div></section>
      ${extraPaymentPlanPage}
      ${buildPromissoryNotesSection({ form, student: info, logoUrl, className: 'combined-notes' })}
      <div class="toolbar"><button onclick="window.print()">Sözleşme ve Senetleri Yazdır / PDF</button></div>
    </body></html>`;
};

const promissoryNotesStyles = `
  .notes-page { width: 210mm; height: 297mm; margin: 0 auto; padding: 6mm; background: white; overflow: hidden; }
  .senet-ledger { height: 285mm; border: 2.2mm solid #c72e45; padding: 5mm; color: #171923; }
  .ledger-header { display: grid; grid-template-columns: 31mm 1fr 31mm; align-items: center; min-height: 37mm; border-bottom: 2px solid #303241; padding-bottom: 2mm; text-align: center; }
  .ledger-logo img { width: 29mm; height: 29mm; object-fit: contain; }
  .ledger-title { font-size: 20pt; font-weight: 900; letter-spacing: 1px; }
  .ledger-institution { color: #c72e45; font-size: 12pt; font-weight: 900; margin-top: 1mm; }
  .ledger-no { font-size: 8pt; line-height: 1.5; text-align: right; }
  .ledger-no strong { font-size: 11pt; }
  .ledger-meta { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #303241; margin-top: 3mm; font-size: 8.5pt; }
  .ledger-meta div { min-height: 7mm; padding: 1.2mm 2mm; border-bottom: 1px solid #777; }
  .ledger-meta div:nth-child(odd) { border-right: 1px solid #777; }
  .ledger-meta div:nth-last-child(-n + 2) { border-bottom: 0; }
  .ledger-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 3mm; font-size: 7.5pt; }
  .ledger-table th, .ledger-table td { border: 1px solid #303241; height: 7mm; padding: .8mm; text-align: center; }
  .ledger-table th { background: #303241; color: white; font-size: 7pt; }
  .ledger-table th:nth-child(1) { width: 6%; }
  .ledger-table th:nth-child(2) { width: 14%; }
  .ledger-table th:nth-child(3), .ledger-table th:nth-child(4), .ledger-table th:nth-child(5) { width: 15%; }
  .ledger-table th:nth-child(6) { width: 15%; }
  .ledger-table th:nth-child(7) { width: 20%; }
  .ledger-table tbody tr:nth-child(even) td { background: #fae6ea; }
  .ledger-totals { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid #303241; border-top: 0; font-size: 9pt; font-weight: 900; }
  .ledger-totals div { padding: 2mm; text-align: center; }
  .ledger-totals div + div { border-left: 1px solid #303241; }
  .ledger-history-title { margin-top: 3mm; background: #c72e45; color: white; padding: 1.2mm; text-align: center; font-size: 7.5pt; font-weight: 900; }
  .ledger-payments { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 6.6pt; }
  .ledger-payments th, .ledger-payments td { border: 1px solid #303241; min-height: 5mm; padding: .7mm; text-align: center; }
  .ledger-payments th { background: #303241; color: white; font-size: 6.2pt; }
  .ledger-payments th:nth-child(1) { width: 6%; }
  .ledger-payments th:nth-child(2) { width: 15%; }
  .ledger-payments th:nth-child(3) { width: 16%; }
  .ledger-payments th:nth-child(4) { width: 13%; }
  .ledger-payments th:nth-child(5) { width: 17%; }
  .ledger-payments th:nth-child(6) { width: 33%; }
  .ledger-payments tfoot td { font-weight: 900; background: #fae6ea; }
  .ledger-note { margin-top: 2mm; font-size: 7pt; line-height: 1.25; text-align: justify; }
  .ledger-signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 22mm; margin-top: 4mm; font-size: 8pt; }
  .ledger-signatures div { min-height: 18mm; border-top: 1px solid #303241; padding-top: 2mm; display: flex; flex-direction: column; gap: 2mm; }
  @media print { .notes-page { margin: 0; } }
`;

const buildPromissoryNotesSection = ({ form, student, logoUrl = '', className = '' }) => {
  const info = student || form.sozlesme_form_bilgileri || {};
  const documentInfo = normalizeContractDocumentInfo(form.belge_bilgileri || form.belge_bilgileri_json, info, form);
  const installments = buildInstallmentPlan(form).filter(item => item.durum !== 'iptal');
  const debtorName = documentInfo.borclu_ad_soyad || info.veli_adi || info.veli_ad_soyad || info.anne_adi || info.baba_adi || '';
  const issueDate = form.created_at ? String(form.created_at).slice(0, 10) : isoToday();
  const total = installments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const paid = installments.reduce((sum, item) => sum + getInstallmentPaidAmount(item), 0);
  const collectionHistory = buildCollectionHistory(form.odemeler);
  const collectionTotal = collectionHistory.reduce((sum, payment) => sum + Number(payment.tutar || 0), 0);
  const rowCount = Math.max(6, installments.length);
  const rows = Array.from({ length: rowCount }, (_, index) => {
    const installment = installments[index];
    const amount = Number(installment?.amount || 0);
    const paidAmount = getInstallmentPaidAmount(installment);
    return `<tr>
      <td>${index + 1}</td>
      <td>${installment?.date ? dateText(installment.date) : '&nbsp;'}</td>
      <td>${installment ? money(amount) : '&nbsp;'}</td>
      <td>${paidAmount ? money(paidAmount) : '&nbsp;'}</td>
      <td>${installment ? money(amount - paidAmount) : '&nbsp;'}</td>
      <td>${installment?.odeme_tarihi ? dateText(installment.odeme_tarihi) : '&nbsp;'}</td>
      <td>${installment ? escapeHtml(installment.odeme_yontemi || installment.durum || '') : '&nbsp;'}</td>
    </tr>`;
  }).join('');
  const collectionRows = collectionHistory.map((payment, index) => `<tr>
    <td>${index + 1}</td>
    <td>${dateText(payment.tarih)}</td>
    <td>${money(payment.tutar)}</td>
    <td>${escapeHtml(payment.odeme_yontemi || '-')}</td>
    <td>${escapeHtml(payment.makbuz_no || '-')}</td>
    <td>${escapeHtml(String(payment.notlar || '-').slice(0, 90))}</td>
  </tr>`).join('');

  return `<main class="notes-page ${escapeHtml(className)}"><section class="senet-ledger">
    <header class="ledger-header">
      <div class="ledger-logo">${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="YED KURS logosu" />` : ''}</div>
      <div><div class="ledger-title">SENET DEFTERİ</div><div class="ledger-institution">YED KURS</div></div>
      <div class="ledger-no">SÖZLEŞME NO<br /><strong>${escapeHtml(documentInfo.sozlesme_no || form.id || '-')}</strong></div>
    </header>
    <div class="ledger-meta">
      <div><strong>Öğrenci:</strong> ${escapeHtml(getContractStudentName(info))}</div>
      <div><strong>Düzenleme Tarihi:</strong> ${dateText(issueDate)}</div>
      <div><strong>Borçlu / Veli:</strong> ${escapeHtml(debtorName || '-')}</div>
      <div><strong>T.C. Kimlik No:</strong> ${escapeHtml(documentInfo.borclu_tc_kimlik || '-')}</div>
      <div><strong>Ödeme Yeri:</strong> ${escapeHtml(documentInfo.odeme_yeri || '-')}</div>
      <div><strong>Düzenleme Yeri:</strong> ${escapeHtml(documentInfo.duzenleme_yeri || '-')}</div>
    </div>
    <table class="ledger-table">
      <thead><tr><th>NO</th><th>VADE TARİHİ</th><th>SENET TUTARI</th><th>ÖDENEN</th><th>KALAN</th><th>ÖDEME TARİHİ</th><th>AÇIKLAMA / İMZA</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="ledger-totals"><div>TOPLAM: ${money(total)}</div><div>ÖDENEN: ${money(paid)}</div><div>KALAN: ${money(total - paid)}</div></div>
    ${collectionHistory.length ? `<div class="ledger-history-title">TAHSİLAT GEÇMİŞİ — HER PARA ALIMI</div>
    <table class="ledger-payments">
      <thead><tr><th>NO</th><th>TAHSİLAT TARİHİ</th><th>ALINAN TUTAR</th><th>YÖNTEM</th><th>MAKBUZ</th><th>AÇIKLAMA</th></tr></thead>
      <tbody>${collectionRows}</tbody>
      <tfoot><tr><td colspan="2">TOPLAM TAHSİLAT</td><td>${money(collectionTotal)}</td><td colspan="3"></td></tr></tfoot>
    </table>` : ''}
    <div class="ledger-note">Bu çizelgede yer alan taksit tarihleri ve tutarları sözleşmenin ödeme planından alınmıştır. Manuel ödeme planında girilen her taksit tutarı değiştirilmeden kendi satırına aktarılır.</div>
    <div class="ledger-signatures">
      <div><strong>BORÇLU / ÖĞRENCİ VELİSİ</strong><span>Ad Soyad: ${escapeHtml(debtorName || '-')}</span><span>İmza:</span></div>
      <div><strong>YED KURS</strong><span>Kaşe / Yetkili İmza:</span></div>
    </div>
  </section></main>`;
};

export const buildPromissoryNotesHtml = ({ form, student, logoUrl = '' }) => {
  const info = student || form.sozlesme_form_bilgileri || {};

  return `<!doctype html><html lang="tr"><head><meta charset="utf-8" />
    <title>Senet Defteri - ${escapeHtml(getContractStudentName(info))}</title>
    <style>
      ${commonDocumentStyles}
      @page { size: A4 portrait; margin: 0; }
      ${promissoryNotesStyles}
    </style></head><body>${buildPromissoryNotesSection({ form, student: info, logoUrl })}
    <div class="toolbar"><button onclick="window.print()">Senet Defterini Yazdır / PDF</button></div></body></html>`;
};

const openPrintWindow = ({ html, title, width = 1000, height = 1000 }) => {
  try {
    if (typeof window === 'undefined' || typeof window.open !== 'function') return false;
    let printWindow = null;
    try {
      printWindow = window.open('', '_blank', `width=${width},height=${height}`);
    } catch {
      return false;
    }
    if (!printWindow || !printWindow.document) return false;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.document.title = title;
    if (typeof printWindow.print === 'function') {
      setTimeout(() => printWindow.print(), 500);
    }
    return true;
  } catch {
    return false;
  }
};

export const printCourseContractDocument = options => openPrintWindow({
  html: buildCourseContractHtml(options),
  title: `Öğrenci Kayıt Sözleşmesi - ${getContractStudentName(options.student || options.form?.sozlesme_form_bilgileri)}`,
});

export const printPromissoryNotesDocument = options => openPrintWindow({
  html: buildPromissoryNotesHtml(options),
  title: `Senet Defteri - ${getContractStudentName(options.student || options.form?.sozlesme_form_bilgileri)}`,
});

export const paymentMethodLabel = method => ({
  nakit: 'Nakit',
  kredi_karti: 'Kredi Kartı',
  banka_havalesi: 'Banka Havalesi',
  senet: 'Senet',
  pos: 'POS',
  karma: 'Karma / Çeşitli',
  diger: 'Diğer',
}[String(method || '').toLowerCase()] || method || 'Nakit');

export const makeReceiptNo = (item = {}) => {
  const dateStr = isoToday().replace(/-/g, '');
  const rand = Math.floor(1000 + Math.random() * 9000);
  const no = item?.taksit_no || item?.id || '1';
  return `MKB-${dateStr}-${no}-${rand}`;
};

export const numberToTurkishWords = (amount) => {
  const val = Math.round(Number(amount || 0) * 100) / 100;
  if (isNaN(val) || val <= 0) return 'Sıfır TL';

  const lira = Math.floor(val);
  const kurus = Math.round((val - lira) * 100);

  const birler = ['', 'Bir', 'İki', 'Üç', 'Dört', 'Beş', 'Altı', 'Yedi', 'Sekiz', 'Dokuz'];
  const onlar = ['', 'On', 'Yirmi', 'Otuz', 'Kırk', 'Elli', 'Altmış', 'Yetmiş', 'Seksen', 'Doksan'];
  const basamaklar = ['', 'Bin', 'Milyon', 'Milyar'];

  const convertThreeDigits = (num) => {
    let res = '';
    const yuzler = Math.floor(num / 100);
    const kalan = num % 100;
    const onluk = Math.floor(kalan / 10);
    const birlik = kalan % 10;

    if (yuzler === 1) res += 'Yüz ';
    else if (yuzler > 1) res += birler[yuzler] + ' Yüz ';

    if (onluk > 0) res += onlar[onluk] + ' ';
    if (birlik > 0) res += birler[birlik] + ' ';

    return res.trim();
  };

  const convertLira = (num) => {
    if (num === 0) return '';
    let parts = [];
    let temp = num;
    let step = 0;

    while (temp > 0) {
      const chunk = temp % 1000;
      if (chunk > 0) {
        let chunkStr = convertThreeDigits(chunk);
        if (step === 1 && chunk === 1) {
          chunkStr = '';
        }
        const b = basamaklar[step];
        parts.unshift((chunkStr ? chunkStr + (b ? ' ' + b : '') : b).trim());
      }
      temp = Math.floor(temp / 1000);
      step++;
    }
    return parts.join(' ');
  };

  let result = 'Yalnız ' + (lira > 0 ? convertLira(lira) + ' TL' : '');
  if (kurus > 0) {
    const kurusStr = convertThreeDigits(kurus);
    result += (lira > 0 ? ' ' : '') + kurusStr + ' Kuruş';
  }
  return result.trim();
};

export const buildPaymentReceiptHtml = ({
  contract = {},
  student = {},
  payment = {},
  installments = [],
  payments = [],
  cashierName = '',
  logoUrl = '',
}) => {
  const info = student || contract?.sozlesme_form_bilgileri || {};
  const documentInfo = normalizeContractDocumentInfo(
    contract?.belge_bilgileri || contract?.belge_bilgileri_json,
    info,
    contract
  );
  const studentName = getContractStudentName(info) !== '-'
    ? getContractStudentName(info)
    : `${contract.ad || ''} ${contract.soyad || ''}`.trim() || 'Öğrenci';
  const parentName = documentInfo.borclu_ad_soyad
    || info.veli_adi
    || info.veli_ad_soyad
    || contract.veli_adi
    || '-';
  const parentPhone = documentInfo.borclu_telefon
    || info.veli_telefon
    || contract.veli_tel
    || info.telefon
    || '-';
  const className = info.sinif_adi || info.sinif || contract.sinif_adi || '-';
  const packageName = contract.paket || info.paket || '-';
  const academicYear = contract.egitim_yili || info.egitim_yili || '-';
  const contractNo = documentInfo.sozlesme_no || contract.id || '-';

  const isGeneral = Boolean(payment.isGeneralStatement);
  const receiptNo = payment.makbuz_no || makeReceiptNo(payment);
  const paymentDate = dateText(payment.odeme_tarihi || payment.tarih || isoToday());
  const paymentMethod = paymentMethodLabel(payment.odeme_yontemi);
  const paymentAmount = Number(payment.tutar || 0);
  const amountWords = numberToTurkishWords(paymentAmount);
  const notes = payment.notlar || '-';
  const cashier = cashierName
    || (payment.kaydeden_ad ? `${payment.kaydeden_ad} ${payment.kaydeden_soyad || ''}`.trim() : '')
    || (contract.kaydeden_ad ? `${contract.kaydeden_ad} ${contract.kaydeden_soyad || ''}`.trim() : '')
    || 'Yetkili Muhasebe';

  // Overall Contract Balances
  const totalContractFee = Number(contract.net_ucret ?? contract.tutar)
    || (installments || []).reduce((s, t) => s + Number(t.tutar || 0), 0);
  const allPaymentsList = (payments || []).length
    ? payments
    : (paymentAmount > 0 && !isGeneral ? [payment] : []);
  const totalPaidToDate = allPaymentsList.length
    ? allPaymentsList.reduce((s, p) => s + Number(p.tutar || 0), 0)
    : (installments || []).reduce((s, t) => s + (Number(t.odeme_tutari || 0) || (t.durum === 'odendi' ? Number(t.tutar || 0) : 0)), 0);
  const remainingDebt = Math.max(0, totalContractFee - totalPaidToDate);

  // Installment distribution for this specific payment
  let targetInstallments = [];
  if (Array.isArray(payment.items) && payment.items.length) {
    targetInstallments = payment.items;
  } else if (Array.isArray(payment.taksitNolari) && payment.taksitNolari.length) {
    targetInstallments = (installments || []).filter(t => payment.taksitNolari.includes(Number(t.taksit_no)));
  } else if (payment.taksit_no || payment.taksit_id) {
    const match = (installments || []).find(t => String(t.taksit_no) === String(payment.taksit_no) || String(t.id) === String(payment.taksit_id));
    if (match) targetInstallments = [match];
  }

  // Open / Remaining installments
  const openInstallments = (installments || []).filter(t => {
    const rem = Number(t.kalan_tutar ?? (Number(t.tutar || 0) - Number(t.odeme_tutari || 0)));
    return t.durum !== 'odendi' && t.durum !== 'iptal' && rem > 0;
  });

  return `<!doctype html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <title>Tahsilat Makbuzu - ${escapeHtml(receiptNo)} - ${escapeHtml(studentName)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #f1f5f9;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #0f172a;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .receipt-container {
      max-width: 800px;
      margin: 24px auto;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
      padding: 32px 36px;
    }
    .toolbar {
      max-width: 800px;
      margin: 14px auto 0;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
    .toolbar button {
      border: 0;
      border-radius: 8px;
      padding: 10px 20px;
      font-weight: 800;
      font-size: 14px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .btn-print { background: #c72e45; color: white; }
    .btn-close { background: #e2e8f0; color: #334155; }
    
    .receipt-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 18px;
      border-bottom: 2px solid #0f172a;
      gap: 16px;
    }
    .inst-brand {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .inst-brand img {
      max-height: 54px;
      width: auto;
      object-fit: contain;
    }
    .inst-name {
      font-size: 22px;
      font-weight: 900;
      color: #c72e45;
      letter-spacing: -0.5px;
      line-height: 1.1;
    }
    .inst-sub {
      font-size: 11px;
      color: #475569;
      font-weight: 700;
      text-transform: uppercase;
      margin-top: 4px;
      letter-spacing: 0.5px;
    }
    .receipt-meta {
      text-align: right;
      font-size: 13px;
      line-height: 1.5;
    }
    .receipt-title {
      font-size: 20px;
      font-weight: 900;
      color: #0f172a;
      letter-spacing: -0.3px;
      margin-bottom: 4px;
    }
    .receipt-badge {
      display: inline-block;
      font-family: monospace;
      font-weight: 700;
      font-size: 13px;
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      padding: 2px 8px;
    }

    .grid-info {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      margin: 20px 0;
    }
    .info-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 14px;
    }
    .info-label {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      color: #64748b;
      margin-bottom: 4px;
      letter-spacing: 0.5px;
    }
    .info-val {
      font-size: 14px;
      font-weight: 800;
      color: #0f172a;
    }
    .info-sub {
      font-size: 12px;
      color: #64748b;
      margin-top: 2px;
    }

    .amount-highlight {
      grid-column: span 2;
      background: #f0fdf4;
      border: 1.5px solid #22c55e;
      border-radius: 8px;
      padding: 14px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .amount-val {
      font-size: 26px;
      font-weight: 900;
      color: #15803d;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    .amount-words {
      font-size: 13px;
      font-weight: 700;
      color: #166534;
      margin-top: 3px;
    }

    .section-title {
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
      color: #334155;
      margin: 20px 0 8px;
      letter-spacing: 0.6px;
      border-bottom: 1.5px solid #e2e8f0;
      padding-bottom: 5px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-top: 4px;
    }
    th {
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      padding: 8px 10px;
      text-align: left;
      font-weight: 800;
      color: #334155;
      font-size: 11px;
    }
    td {
      border: 1px solid #e2e8f0;
      padding: 8px 10px;
      text-align: left;
    }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-mono { font-family: monospace; }
    
    .totals-bar {
      display: flex;
      justify-content: flex-end;
      gap: 20px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 18px;
      margin-top: 14px;
    }
    .total-item { text-align: right; }
    .total-item-label { font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase; }
    .total-item-val { font-size: 16px; font-weight: 900; }
    
    .all-paid-badge {
      background: #ecfdf5;
      color: #065f46;
      border: 1px solid #a7f3d0;
      border-radius: 6px;
      padding: 8px 14px;
      font-weight: 700;
      font-size: 12px;
      margin-top: 10px;
      text-align: center;
    }

    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      margin-top: 40px;
      padding-top: 10px;
    }
    .sig-box {
      text-align: center;
      border-top: 1.5px solid #0f172a;
      padding-top: 8px;
    }
    .sig-title {
      font-weight: 800;
      font-size: 12px;
      text-transform: uppercase;
      color: #0f172a;
    }
    .sig-sub {
      font-size: 11px;
      color: #64748b;
      margin-top: 2px;
    }
    .sig-space { height: 50px; }

    .receipt-footer-note {
      margin-top: 26px;
      text-align: center;
      font-size: 11px;
      color: #64748b;
      font-style: italic;
      border-top: 1px dashed #cbd5e1;
      padding-top: 10px;
    }

    @media print {
      body { background: #ffffff; padding: 0; }
      .receipt-container {
        border: none;
        box-shadow: none;
        margin: 0;
        max-width: 100%;
        padding: 10mm 15mm;
        border-radius: 0;
      }
      .toolbar { display: none !important; }
      @page { size: A4 portrait; margin: 10mm 12mm; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">🖨️ Makbuzu Yazdır / PDF</button>
    <button class="btn-close" onclick="window.close()">✕ Kapat</button>
  </div>

  <div class="receipt-container">
    <header class="receipt-header">
      <div class="inst-brand">
        ${logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Logo" />` : ''}
        <div>
          <div class="inst-name">${escapeHtml(documentInfo.kurum_unvani || 'YED KURS')}</div>
          <div class="inst-sub">Özel Öğretim Kursu · Osmaniye</div>
        </div>
      </div>
      <div class="receipt-meta">
        <div class="receipt-title">${isGeneral ? 'GENEL TAHSİLAT EKSTRESİ' : 'TAHSİLAT MAKBUZU'}</div>
        <div><strong>Makbuz No:</strong> <span class="receipt-badge">${escapeHtml(receiptNo)}</span></div>
        <div style="margin-top: 4px;"><strong>Tarih:</strong> ${escapeHtml(paymentDate)}</div>
        <div><strong>Ödeme Yöntemi:</strong> <span style="font-weight: 700;">${escapeHtml(paymentMethod)}</span></div>
      </div>
    </header>

    <main>
      <div class="grid-info">
        <div class="info-card">
          <div class="info-label">Öğrenci Bilgileri</div>
          <div class="info-val">${escapeHtml(studentName)}</div>
          <div class="info-sub">Sınıf: ${escapeHtml(className)} · Paket: ${escapeHtml(packageName)}</div>
          <div class="info-sub">Sözleşme No: #${escapeHtml(contractNo)} · Yıl: ${escapeHtml(academicYear)}</div>
        </div>

        <div class="info-card">
          <div class="info-label">Parayı Ödeyen (Veli / Borçlu)</div>
          <div class="info-val">${escapeHtml(parentName)}</div>
          <div class="info-sub">İletişim: ${escapeHtml(parentPhone)}</div>
          <div class="info-sub">Tahsil Eden: ${escapeHtml(cashier)}</div>
        </div>

        <div class="amount-highlight">
          <div>
            <div class="info-label" style="color: #166534;">Tahsil Edilen Tutar</div>
            <div class="amount-val">${money(paymentAmount)}</div>
          </div>
          <div style="text-align: right;">
            <div class="info-label" style="color: #166534;">Yazı İle</div>
            <div class="amount-words">${escapeHtml(amountWords)}</div>
            ${notes && notes !== '-' ? `<div style="font-size: 11px; color: #475569; margin-top: 4px;">Not: ${escapeHtml(notes)}</div>` : ''}
          </div>
        </div>
      </div>

      ${isGeneral && allPaymentsList.length ? `
        <div class="section-title">
          <span>Bugüne Kadar Yapılan Tüm Tahsilatlar</span>
          <span style="font-size: 11px; font-weight: normal; color: #64748b;">Toplam ${allPaymentsList.length} ödeme kaydı</span>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 35px;" class="text-center">#</th>
              <th>Tahsilat Tarihi</th>
              <th>Taksit #</th>
              <th>Ödeme Yöntemi</th>
              <th>Makbuz No</th>
              <th class="text-right">Tutar</th>
            </tr>
          </thead>
          <tbody>
            ${allPaymentsList.map((p, idx) => `
              <tr>
                <td class="text-center">${idx + 1}</td>
                <td>${dateText(p.odeme_tarihi || p.tarih)}</td>
                <td>#${escapeHtml(p.taksit_no || p.taksitLabel || '-')}</td>
                <td>${escapeHtml(paymentMethodLabel(p.odeme_yontemi))}</td>
                <td class="font-mono">${escapeHtml(p.makbuz_no || '-')}</td>
                <td class="text-right" style="font-weight: 700; color: #15803d;">${money(p.tutar)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : targetInstallments.length ? `
        <div class="section-title">
          <span>Bu Tahsilatın İlişkili Olduğu Taksit(ler)</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Taksit No</th>
              <th>Vade Tarihi</th>
              <th class="text-right">Taksit Plan Tutarı</th>
              <th class="text-right">Ödenen</th>
              <th class="text-right">Bu Taksitte Kalan</th>
              <th class="text-center">Durum</th>
            </tr>
          </thead>
          <tbody>
            ${targetInstallments.map(t => {
              const rem = Math.max(0, Number(t.kalan_tutar ?? (Number(t.tutar || 0) - Number(t.odeme_tutari || 0))));
              const isPaid = rem <= 0 || t.durum === 'odendi';
              return `
                <tr>
                  <td style="font-weight: 800;">#${escapeHtml(t.taksit_no || '-')} Taksit</td>
                  <td>${dateText(t.vade_tarihi)}</td>
                  <td class="text-right">${money(t.tutar)}</td>
                  <td class="text-right" style="font-weight: 700; color: #15803d;">${money(t.odeme_tutari || paymentAmount)}</td>
                  <td class="text-right" style="font-weight: 700; color: ${rem > 0 ? '#b91c1c' : '#15803d'};">${money(rem)}</td>
                  <td class="text-center">
                    <span style="font-weight: 700; color: ${isPaid ? '#15803d' : '#b45309'};">
                      ${isPaid ? 'Ödendi' : 'Kısmi Ödendi'}
                    </span>
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      ` : ''}

      <div class="section-title">
        <span>Sözleşme Genel Hesap ve Bakiye Durumu</span>
      </div>
      <div class="totals-bar">
        <div class="total-item">
          <div class="total-item-label">Sözleşme Toplam Ücreti</div>
          <div class="total-item-val" style="color: #0f172a;">${money(totalContractFee)}</div>
        </div>
        <div class="total-item">
          <div class="total-item-label">Bugüne Kadar Ödenen</div>
          <div class="total-item-val" style="color: #15803d;">${money(totalPaidToDate)}</div>
        </div>
        <div class="total-item">
          <div class="total-item-label">Toplam Kalan Borç</div>
          <div class="total-item-val" style="color: ${remainingDebt > 0 ? '#dc2626' : '#15803d'};">
            ${money(remainingDebt)}
          </div>
        </div>
      </div>

      ${remainingDebt <= 0 ? `
        <div class="all-paid-badge">
          ✓ Bu sözleşmeye ait tüm taksitler ve ödemeler tamamlanmış olup öğrencinin kuruma herhangi bir bakiye borcu kalmamıştır.
        </div>
      ` : openInstallments.length > 0 ? `
        <div style="margin-top: 14px;">
          <div style="font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 4px;">ÖDENECEK KALAN TAKSİTLER:</div>
          <table>
            <thead>
              <tr>
                <th>Taksit</th>
                <th>Vade Tarihi</th>
                <th class="text-right">Kalan Tutar</th>
              </tr>
            </thead>
            <tbody>
              ${openInstallments.map(t => {
                const rem = Math.max(0, Number(t.kalan_tutar ?? (Number(t.tutar || 0) - Number(t.odeme_tutari || 0))));
                return `
                  <tr>
                    <td>#${escapeHtml(t.taksit_no)} Taksit</td>
                    <td>${dateText(t.vade_tarihi)}</td>
                    <td class="text-right" style="font-weight: 700; color: #b91c1c;">${money(rem)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      <div class="signatures">
        <div class="sig-box">
          <div class="sig-space"></div>
          <div class="sig-title">TESLİM EDEN (ÖĞRENCİ / VELİ)</div>
          <div class="sig-sub">${escapeHtml(parentName)}</div>
          <div class="sig-sub" style="font-size: 10px; margin-top: 4px;">İmza</div>
        </div>
        <div class="sig-box">
          <div class="sig-space"></div>
          <div class="sig-title">TESLİM ALAN (YED KURS)</div>
          <div class="sig-sub">${escapeHtml(cashier)}</div>
          <div class="sig-sub" style="font-size: 10px; margin-top: 4px;">Kaşe / Yetkili İmza</div>
        </div>
      </div>

      <div class="receipt-footer-note">
        İşbu tahsilat makbuzu, kurumumuza yapılan ödemenin resmi tevsikidir. Belgenin bir nüshası veliye teslim edilmiştir.
      </div>
    </main>
  </div>
</body>
</html>`;
};

export const printPaymentReceiptDocument = options => openPrintWindow({
  html: buildPaymentReceiptHtml(options),
  title: `Tahsilat Makbuzu - ${options.payment?.makbuz_no || 'YED KURS'} - ${getContractStudentName(options.student || options.contract?.sozlesme_form_bilgileri)}`,
});

